<template>
  <section class="analytics-products-table">
    <div class="sticky-header">
      <!-- Header / Filters -->
      <div class="list-header">
        <div class="ui-search-wrap">
          <Search @onenter="onSearch" :disabled="is_loading" :placeholder_text="search_placeholder" />
        </div>
        <div class="list-btn-box">
          <DropdownBrands
            v-if="is_advertisers_route && start_date && end_date"
            :start_date="start_date"
            :end_date="end_date"
            :direction_type="'right'"
            :brand_ids="brand_ids"
          />
          <Dropdown
            v-if="show_sold_by && store.state.show_advertisement"
            :items="sold_by_items"
            :direction_type="'right'"
            :icon_direction_type="'right'"
            :other_text="'Advertiser: '"
            @update-dropdown="onSoldByChange"
          />
          <Dropdown
            :items="product_sort_items"
            :direction_type="'right'"
            :icon="'filter'"
            :other_text="''"
            :default_value="sort_index"
            @update-dropdown="onSortChange"
          />
          <Dropdown
            :items="per_page_items"
            :direction_type="'right'"
            :no_icon="true"
            :other_text="''"
            @update-dropdown="onPerPageChange"
          />
          <Pagenavi
            :disabled="last_page === 1"
            :page_current="current_page"
            :page_total="last_page"
            @goto-page="gotoPage"
          />
        </div>
      </div>
      <!-- Stats bar -->
      <div class="short-link">
        <div class="report-box">
          <Loading v-if="is_loading" class="absolute" />
          <div class="report-item">
            <SValue
              :is_wrap="true"
              :sum="display_statistic.storefront_count"
              :value_text="'Storefronts'"
              :show_sortlink="false"
            />
          </div>
          <div class="report-item" v-if="!is_link_only">

            <SValue
              :chart_type_text="'views'"
              :is_wrap="true"
              :sum="display_statistic.views_sum"
              :value_text="'Product Views'"
              :show_sortlink="false"
            />
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'click'"
              :is_wrap="true"
              :sum="display_statistic.all_click"
              :value_text="'Clicks'"
              :show_sortlink="sortlink_data === 'all' && display_statistic.can_toggle_sortlink"
              :sort_value="display_statistic.short_link_click_sum || 0"
              :store_value="display_statistic.click_sum || 0"
            />
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'tran'"
              :is_wrap="true"
              :sum="display_statistic.all_sales"
              :value_text="'Sales'"
              :show_sortlink="sortlink_data === 'all' && display_statistic.can_toggle_sortlink"
              :sort_value="display_statistic.short_link_sales_sum || 0"
              :store_value="display_statistic.sales_sum || 0"
            />
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'sales'"
              :is_wrap="true"
              :sum="display_statistic.all_sales_value"
              :value_text="'Sales Value'"
              :show_sortlink="sortlink_data === 'all' && display_statistic.can_toggle_sortlink"
              :sort_value="Math.ceil(display_statistic.short_link_sales_value_sum || 0)"
              :store_value="Math.ceil(display_statistic.sales_value_sum || 0)"
            />
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'earnings'"
              :is_wrap="true"
              :sum="display_statistic.all_earnings"
              :value_text="'Earnings'"
              :show_sortlink="sortlink_data === 'all' && display_statistic.can_toggle_sortlink"
              :sort_value="Math.ceil(display_statistic.short_link_earnings_sum || 0)"
              :store_value="Math.ceil(display_statistic.earnings_sum || 0)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <Loading v-if="is_loading && !data_statistic" class="static" />

    <!-- Table -->
    <Scrollbar class="data-list-scroll" :show_bar="true">
      <div v-if="data.length !== 0" class="product-table-view">
        <Loading v-if="is_loading" class="absolute" />
        <Table>
          <template #head>
            <tr>
              <th class="name-row">
                <span>Products ({{ total }})</span>
              </th>
              <TableTh
                v-if="store.state.show_advertisement"
                name="merchant_name"
                title="Advertiser"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Advertiser"
              />
              <TableTh
                name="creator_count"
                title="Creators"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Creators"
              />
              <TableTh
                v-if="!is_link_only"
                name="views"
                title="Product Views"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Product Views"
              />
              <TableTh
                name="click"
                title="Product Clicks"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Product Clicks"
              />
              <TableTh name="sales" title="Sales" :sort_value="sort" @sort="onColumnSort" content="Sales" />
              <TableTh
                name="sales_value"
                title="Sales Value"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Sales Value"
              />
              <TableTh
                name="earnings"
                title="Earnings"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Earnings"
              />
            </tr>
          </template>

          <template #body>
            <tr v-for="(item, index) in data" :key="index" @click.stop="openDetail(item)" style="cursor: pointer;">
              <td class="name-row">
                <div class="product-name-cell">
                  <div
                    class="product-thumb"
                    :style="{ backgroundImage: `url(${item.image_url || item.product?.images?.img_400x400 || item.product?.thumbnail || 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?q=80&w=870&auto=format&fit=crop'})`, backgroundSize: 'cover', backgroundPosition: 'center' }"
                  ></div>
                  <div class="product-info">
                    <div
                      class="product-title"
                      v-tippy="{ content: item.product.name, delay: [500, 20], onShow: showTippyIfTruncated }"
                    >{{ item.product.name }}</div>
                    <div class="product-brand">{{ item.brand_name }}</div>
                  </div>
                </div>
              </td>
              <td v-if="store.state.show_advertisement">{{ item.merchant_name || '---' }}</td>
              <td>
                <div class="creators-row">
                  <svg-icon name="users-2" />
                  {{ item.creator_count != null ? item.creator_count.toLocaleString('en-US') : '---' }}
                </div>
              </td>
              <td v-if="!is_link_only">{{ item.views ? item.views.toLocaleString('en-US') : '---' }}</td>
              <td>{{ item.click ? item.click.toLocaleString('en-US') : '---' }}</td>
              <td>{{ item.sales ? item.sales.toLocaleString('en-US') : '---' }}</td>
              <td v-if="typeof item.sales_value === 'number'">
                ${{ item.sales_value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
              <td v-if="typeof item.earnings === 'number'">
                ${{ item.earnings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
            </tr>
          </template>

          <template #foot>
            <tr class="total-row" v-if="data_total">
              <td class="name-row">Total: {{ total }}</td>
              <td>---</td>
              <td>{{ data_total.creator_count != null ? data_total.creator_count.toLocaleString('en-US') : '---' }}</td>
              <td v-if="!is_link_only">{{ data_total.views ? data_total.views.toLocaleString('en-US') : '---' }}</td>
              <td>{{ data_total.click ? data_total.click.toLocaleString('en-US') : '---' }}</td>
              <td>{{ data_total.sales ? data_total.sales.toLocaleString('en-US') : '---' }}</td>
              <td v-if="typeof data_total.sales_value === 'number'">
                ${{ data_total.sales_value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
              <td v-if="typeof data_total.earnings === 'number'">
                ${{ data_total.earnings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
            </tr>
          </template>
        </Table>
      </div>

      <div v-if="!is_loading && data.length === 0">
        <NoData :image="require('@/assets/images/pc.png')" title="No data available" text="" />
      </div>
    </Scrollbar>
  </section>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useAnalyticsProductList, per_page_items } from '@/composables/useAnalyticsProductList.js';

const sold_by_items = [
  { text: 'All', value: '' },
  { text: 'Creators', value: 'creator' },
  { text: 'Advertisers', value: 'advertiser' },
  { text: 'Brands', value: 'brand' },
];

const product_sort_items = [
  { text: 'Sort: Product Views (Descending)', value: 'views_desc' },
  { text: 'Sort: Product Views (Ascending)', value: 'views_asc' },
  { text: 'Sort: Clicks (Descending)', value: 'click_desc' },
  { text: 'Sort: Clicks (Ascending)', value: 'click_asc' },
  { text: 'Sort: Sales (Descending)', value: 'sales_desc' },
  { text: 'Sort: Sales (Ascending)', value: 'sales_asc' },
  { text: 'Sort: Sales Value (Descending)', value: 'sales_value_desc' },
  { text: 'Sort: Sales Value (Ascending)', value: 'sales_value_asc' },
  { text: 'Sort: Earnings (Descending)', value: 'earnings_desc' },
  { text: 'Sort: Earnings (Ascending)', value: 'earnings_asc' },
];
import Scrollbar from '@/components/ui/Scrollbar.vue';
import Search from '@/components/ui/Search.vue';
import Dropdown from '@/components/ui/Dropdown.vue';
import DropdownBrands from '@/components/ui/DropdownBrands.vue';
import Pagenavi from '@/components/ui/Pagenavi.vue';
import Loading from '@/components/ui/Loading.vue';
import NoData from '@/components/creator/NoData.vue';
import Table from '@/components/ui/Table.vue';
import TableTh from '@/components/ui/TableTh.vue';
import SValue from '@/components/summary/SummaryValue.vue';

const props = defineProps({
  search_placeholder: { type: String, default: 'Search by product name' },
  build_statistic_url: { type: Function, required: true },
  build_list_url: { type: Function, required: true },
  start_date: { type: String, default: '' },
  end_date: { type: String, default: '' },
  show_sold_by: { type: Boolean, default: false },
  brand_filter_items: { type: Array, default: () => [] },
  filter_other_text: { type: String, default: 'Brand: ' },
  brand_ids: { type: Array, default: () => [] },
});

const mock_statistic = {
  storefront_count: 2390,
  views_sum: 2390, storefront_views_sum: 24000, short_link_views_sum: 2000,
  click_sum: 2390, storefront_click_sum: 24000, short_link_click_sum: 2000,
  sales_sum: 2390, storefront_sales_sum: 24000, short_link_sales_sum: 2000,
  sales_value_sum: 20390, storefront_sales_value_sum: 24000, short_link_sales_value_sum: 2000,
  earnings_sum: 21390, storefront_earnings_sum: 24000, short_link_earnings_sum: 2000,
};

const store = useStore();
const route = useRoute();
const is_advertisers_route = computed(() => route.name?.toLowerCase().includes('advertisers') || route.path?.toLowerCase().includes('advertisers'));
const { proxy } = getCurrentInstance();
const EventBus = proxy.EventBus;

// 只有品名真的被兩行截斷時才顯示 tippy
const showTippyIfTruncated = (instance) => {
  const el = instance.reference;
  return el.scrollHeight > el.clientHeight + 1;
};

const openDetail = (item) => {
  EventBus.emit('open-product-modal', {
    product_id: item.id,
    start_date: props.start_date || store.state.save_analytics_start_date,
    end_date: props.end_date || store.state.save_analytics_end_date,
  });
};

const {
  is_loading, data, data_statistic, data_total,
  current_page, last_page, total,
  sort, sort_index, sortlink_data,
  is_link_only,
  onSearch, onSortChange, onColumnSort, onBrandsChange: onBrandChange, onSoldByChange,
  onPerPageChange, gotoPage,
} = useAnalyticsProductList({
  buildStatisticUrl: props.build_statistic_url,
  buildListUrl: props.build_list_url,
});

const _onUpdateByBrands = (brands) => {
  const id = brands.length > 0 ? String(brands[0].id) : '';
  onBrandChange(brands);
};

onMounted(() => {
  EventBus.on('update-by-brands', _onUpdateByBrands);
});

onBeforeUnmount(() => {
  EventBus.off('update-by-brands', _onUpdateByBrands);
});

const display_statistic = computed(() => {
  const stat = data_statistic.value || mock_statistic;
  return {
    ...stat,
    storefront_count: stat.storefront_count ?? stat.store_front_count,
    views_sum: stat.views_sum ?? stat.product_views_sum,
  };
});
</script>

<style lang="scss">
.analytics-products-table {
  padding-bottom: 24px;

  .sticky-header {
    position: sticky;
    top: 0;
    background: white;
    z-index: 20;
  }

  .list-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 13px 0;

    .list-btn-box {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: auto;
    }
  }

  .ui-search-wrap {
    flex: 1;

    .ui-search-input .input {
      max-width: unset;
    }
  }

  .short-link {
    margin: 12px 8px;

    .report-box {
      display: flex;
      position: relative;
      border: 1px solid rgba(0, 0, 0, 0.08);
      margin-bottom: 16px;

      .report-item {
        flex: 1;
        padding: 12px;
        border-right: 1px solid rgba(0, 0, 0, 0.08);

        &:last-child {
          border-right: none;
        }

        .datainfo-wrap {
            .text {
              font:var(--font-med-14);
              color: var(--text-secondary);
              margin-bottom: 4px;
            }

            .desc-text{
              font:var(--font-med-12);
            }
            .value {
              font: var(--font-med-20);
              color: var(--dark-blue);
            }
        }


        .the-type {
          width: 100%;
          border: none;
          padding: 0;
          background: transparent;
          margin-top: 8px;
        }
      }
    }
  }

  .data-list-scroll {
    overflow-y: visible;
    height: calc(98vh - 360px);
  }

  .product-table-view {
    margin-top: 8px;
    cursor: pointer;
    position: relative;

    .ui-table thead {
      position: sticky;
      top: 0;
      background: white;
      z-index: 10;
    }

    .name-row {
      width: 384px;

      // 表格為 auto layout，靠固定內容寬度鎖住欄寬，避免長品名把後面欄位擠出去
      .product-name-cell {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 360px;

        .product-thumb {
          width: 36px;
          height: 36px;
          border-radius: 4px;
          flex-shrink: 0;
          background: #e0e0e0;
        }

        .product-info {
          min-width: 0;

          .product-title {
            font-size: 13px;
            font-weight: 500;
            color: var(--black-90);
            line-height: 1.4;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            word-break: break-word;
          }

          .product-brand {
            font-size: 11px;
            color: var(--black-50);
            line-height: 1.4;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }

    .creators-row {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    svg {
      width: 12px;
      height: 12px;
      margin-right: 4px;
      vertical-align: middle;
    }
  }

  .tfoot tr td {
    vertical-align: top;
  }
}
</style>
